import { useEffect, useState, SyntheticEvent } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import { getStudent, updateStudent } from '../../api/students';
import { listLookup } from '../../api/masterData';
import { UpdateStudentPayload } from '../../types/student';
import { DetailHeroHeader } from '../../components/DetailHeroHeader';
import { MarkGraduatedDialog } from '../alumni/MarkGraduatedDialog';
import { useAuth } from '../../context/authContext';
import { StudentDocumentsTab } from './StudentDocumentsTab';
import { StudentAcademicTab } from './StudentAcademicTab';
import { StudentPaymentsTab } from './StudentPaymentsTab';
import { StudentTimelineTab } from './StudentTimelineTab';
import { FinalDocumentChecklistPanel } from '../final-documentation/FinalDocumentChecklistPanel';
import { PortalInvitationDialog } from './PortalInvitationDialog';
import { StudentDropoutDialog } from './StudentDropoutDialog';
import { isTemporaryNumber, TEMPORARY_LABEL } from '../../components/temporaryNumber';

const toDateInputValue = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

export const StudentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const studentId = Number(id);
  const queryClient = useQueryClient();
  const { hasPermission, user } = useAuth();
  // Official dropout: management/admissions staff with students:update —
  // never Counselors (the API enforces the same).
  const canRecordDropout = (s: { isDraft: boolean }) =>
    !s.isDraft && hasPermission('students', 'update') && user?.role.name !== 'Counselor';
  const [searchParams] = useSearchParams();

  // Batch progress correction (Phase 64) — Ready to Fly's batch detail
  // view links "View student profile" straight to the Academic tab
  // (`?tab=academic`), so an Officer editing a batched student's
  // milestones never lands on the generic Details tab first.
  const [tab, setTab] = useState(searchParams.get('tab') ?? 'details');
  // Notification links (Phase 94) can open another tab of the student
  // already on screen (only the query string changes), so keep in sync.
  const tabParam = searchParams.get('tab');
  useEffect(() => {
    if (tabParam) setTab(tabParam);
  }, [tabParam]);
  const highlightPaymentId = Number(searchParams.get('paymentId')) || null;
  const highlightTransferId = Number(searchParams.get('transfer')) || null;
  const [values, setValues] = useState<UpdateStudentPayload | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const studentQuery = useQuery({ queryKey: ['student', studentId], queryFn: () => getStudent(studentId) });
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
  });

  useEffect(() => {
    if (studentQuery.data) {
      const s = studentQuery.data;
      setValues({
        firstName: s.firstName,
        lastName: s.lastName,
        email: s.email ?? '',
        phone: s.phone,
        dateOfBirth: toDateInputValue(s.dateOfBirth),
        gender: s.gender ?? undefined,
        // Nationality is the student's own (nationalityCountry). `countryId`
        // is the preferred study destination — it used to be shown and saved
        // here as "Nationality", which is how students heading to a Belarusian
        // university appeared as Belarusian.
        nationalityCountryId: s.nationalityCountry?.id,
        countryId: s.country?.id,
        address: s.address ?? '',
        passportNumber: s.passportNumber ?? '',
        passportExpiryDate: toDateInputValue(s.passportExpiryDate),
        fatherName: s.fatherName ?? '',
        fatherPhone: s.fatherPhone ?? '',
        motherName: s.motherName ?? '',
        motherPhone: s.motherPhone ?? '',
        guardianName: s.guardianName ?? '',
        guardianRelationship: s.guardianRelationship ?? '',
        guardianPhone: s.guardianPhone ?? '',
        guardianEmail: s.guardianEmail ?? '',
        emergencyContactName: s.emergencyContactName ?? '',
        emergencyContactPhone: s.emergencyContactPhone ?? '',
        emergencyContactRelationship: s.emergencyContactRelationship ?? '',
      });
    }
  }, [studentQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (payload: UpdateStudentPayload) => updateStudent(studentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student', studentId] });
      setSaveError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) =>
      setSaveError(isAxiosError(err) ? (err.response?.data?.message ?? 'Failed to save') : 'Failed to save'),
  });

  const [portalDialogOpen, setPortalDialogOpen] = useState(false);
  // Alumni Management (Phase 105) — Mark as Graduated.
  const [graduateOpen, setGraduateOpen] = useState(false);
  // Official dropout (Management Reports).
  const [dropoutMode, setDropoutMode] = useState<'record' | 'reverse' | null>(null);

  if (studentQuery.isLoading) return <CircularProgress sx={{ m: 3 }} />;
  // Checked before `values`: a deleted or out-of-scope student (404) never
  // produces form values, so waiting on them would spin forever.
  if (studentQuery.isError || !studentQuery.data) {
    return (
      <Alert
        severity="error"
        sx={{ m: 3 }}
        action={
          <Button color="inherit" size="small" component={RouterLink} to="/students">
            All students
          </Button>
        }
      >
        This student no longer exists, or you don't have access to their record.
      </Alert>
    );
  }
  if (!values) return <CircularProgress sx={{ m: 3 }} />;

  const student = studentQuery.data;
  const setField = (name: keyof UpdateStudentPayload, value: string | number | undefined) =>
    setValues((prev) => (prev ? { ...prev, [name]: value } : prev));

  return (
    <Box sx={{ p: 3 }}>
      <DetailHeroHeader
        icon={SchoolIcon}
        color="#12B76A"
        backLabel="Back to students"
        backTo="/students"
        title={`${student.firstName} ${student.lastName}`}
        chips={
          <>
            {isTemporaryNumber(student.studentNumber) ? (
              <Tooltip title="The permanent student number is issued once the student is registered and the Registration Fee is approved.">
                <Chip label={`${TEMPORARY_LABEL.student}: ${student.studentNumber}`} color="warning" size="small" data-testid="student-number" />
              </Tooltip>
            ) : (
              <Chip label={student.studentNumber} color="primary" size="small" data-testid="student-number" />
            )}
            {!isTemporaryNumber(student.studentNumber) && student.temporaryStudentNumber && (
              <Chip label={`Temporary no. was ${student.temporaryStudentNumber}`} size="small" variant="outlined" />
            )}
            {student.lead && <Chip label={`From lead ${student.lead.leadNumber}`} size="small" variant="outlined" />}
            {student.graduatedAt && (
              <Chip label={`Graduated ${new Date(student.graduatedAt).toLocaleDateString()}`} size="small" color="success" />
            )}
            {student.droppedOutAt && (
              <Chip label={`Dropped out ${new Date(student.droppedOutAt).toLocaleDateString()}`} size="small" color="error" />
            )}
            {student.alumni && hasPermission('alumni', 'view') && (
              <Chip
                component={RouterLink}
                to={`/alumni/${student.alumni.id}`}
                clickable
                label={`Alumni ${student.alumni.alumniNumber}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </>
        }
      >
        <Typography variant="body2" color="text.secondary">
          Registered by {student.registeredBy.firstName} {student.registeredBy.lastName} on{' '}
          {new Date(student.registeredAt ?? student.createdAt).toLocaleDateString()}
        </Typography>
        {/* Student Portal (Phase 67/68) — provisioned automatically on
            lead-conversion; this dialog covers manual/repeat sends via
            email, WhatsApp or a copyable link, plus the invitation
            history. */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Chip
            size="small"
            label={student.userId ? 'Portal access: set up' : 'Portal access: not set up'}
            color={student.userId ? 'success' : 'default'}
          />
          <Button size="small" onClick={() => setPortalDialogOpen(true)}>
            Manage portal access
          </Button>
          {!student.alumni && !student.isDraft && hasPermission('alumni', 'graduate') && (
            <Button size="small" variant="outlined" onClick={() => setGraduateOpen(true)}>
              Mark as Graduated
            </Button>
          )}
          {canRecordDropout(student) && (
            <Button size="small" color="error" onClick={() => setDropoutMode(student.droppedOutAt ? 'reverse' : 'record')}>
              {student.droppedOutAt ? 'Reverse dropout' : 'Record dropout'}
            </Button>
          )}
        </Stack>
        {student.droppedOutAt && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            Officially recorded as dropped out on {new Date(student.droppedOutAt).toLocaleDateString()}
            {student.dropoutReason ? ` — ${student.dropoutReason}` : ''}.
          </Alert>
        )}
      </DetailHeroHeader>

      <StudentDropoutDialog
        studentId={studentId}
        mode={dropoutMode}
        onClose={() => setDropoutMode(null)}
        onDone={() => {
          setDropoutMode(null);
          void queryClient.invalidateQueries({ queryKey: ['student', studentId] });
        }}
      />

      <MarkGraduatedDialog
        studentId={studentId}
        open={graduateOpen}
        onClose={() => setGraduateOpen(false)}
        onDone={() => setGraduateOpen(false)}
      />

      <PortalInvitationDialog
        open={portalDialogOpen}
        studentId={studentId}
        studentHasEmail={!!student.email}
        onClose={() => setPortalDialogOpen(false)}
      />

      <Tabs value={tab} onChange={(_e: SyntheticEvent, v: string) => setTab(v)}>
        <Tab label="Details" value="details" />
        <Tab label="Academic" value="academic" />
        <Tab label="Payments" value="payments" />
        <Tab label="Documents" value="documents" />
        <Tab label="Final Checklist" value="final-checklist" />
        <Tab label="Timeline" value="timeline" />
      </Tabs>

      {tab === 'details' && (
        <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
          <Stack spacing={2}>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            {saved && <Alert severity="success">Saved.</Alert>}

            <Typography variant="subtitle2">Basic details</Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="First name" fullWidth value={values.firstName} onChange={(e) => setField('firstName', e.target.value)} />
              <TextField label="Last name" fullWidth value={values.lastName} onChange={(e) => setField('lastName', e.target.value)} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Phone" fullWidth value={values.phone} onChange={(e) => setField('phone', e.target.value)} />
              <TextField label="Email" fullWidth value={values.email} onChange={(e) => setField('email', e.target.value)} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Date of birth"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={values.dateOfBirth}
                onChange={(e) => setField('dateOfBirth', e.target.value)}
              />
              <TextField
                select
                label="Nationality"
                fullWidth
                value={values.nationalityCountryId ?? ''}
                onChange={(e) => setField('nationalityCountryId', e.target.value ? Number(e.target.value) : undefined)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {countriesQuery.data?.map((c) => (
                  <MenuItem key={c.id as number} value={c.id as number}>
                    {String(c.name)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              select
              label="Preferred study country"
              helperText="Where the student wants to study — separate from their nationality."
              fullWidth
              value={values.countryId ?? ''}
              onChange={(e) => setField('countryId', e.target.value ? Number(e.target.value) : undefined)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {countriesQuery.data?.map((c) => (
                <MenuItem key={c.id as number} value={c.id as number}>
                  {String(c.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Address" fullWidth value={values.address} onChange={(e) => setField('address', e.target.value)} />

            <Divider />
            <Typography variant="subtitle2">Passport</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Passport number"
                fullWidth
                value={values.passportNumber}
                onChange={(e) => setField('passportNumber', e.target.value)}
              />
              <TextField
                label="Passport expiry"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={values.passportExpiryDate}
                onChange={(e) => setField('passportExpiryDate', e.target.value)}
              />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Parents</Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="Father's name" fullWidth value={values.fatherName} onChange={(e) => setField('fatherName', e.target.value)} />
              <TextField label="Father's phone" fullWidth value={values.fatherPhone} onChange={(e) => setField('fatherPhone', e.target.value)} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Mother's name" fullWidth value={values.motherName} onChange={(e) => setField('motherName', e.target.value)} />
              <TextField label="Mother's phone" fullWidth value={values.motherPhone} onChange={(e) => setField('motherPhone', e.target.value)} />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Guardian</Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="Guardian name" fullWidth value={values.guardianName} onChange={(e) => setField('guardianName', e.target.value)} />
              <TextField
                label="Relationship"
                fullWidth
                value={values.guardianRelationship}
                onChange={(e) => setField('guardianRelationship', e.target.value)}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Guardian phone" fullWidth value={values.guardianPhone} onChange={(e) => setField('guardianPhone', e.target.value)} />
              <TextField label="Guardian email" fullWidth value={values.guardianEmail} onChange={(e) => setField('guardianEmail', e.target.value)} />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Emergency contact</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Name"
                fullWidth
                value={values.emergencyContactName}
                onChange={(e) => setField('emergencyContactName', e.target.value)}
              />
              <TextField
                label="Relationship"
                fullWidth
                value={values.emergencyContactRelationship}
                onChange={(e) => setField('emergencyContactRelationship', e.target.value)}
              />
            </Stack>
            <TextField
              label="Phone"
              fullWidth
              value={values.emergencyContactPhone}
              onChange={(e) => setField('emergencyContactPhone', e.target.value)}
            />

            <Button
              variant="contained"
              sx={{ alignSelf: 'flex-start' }}
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(values)}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Paper>
      )}

      {tab === 'academic' && <StudentAcademicTab studentId={studentId} highlightTransferId={highlightTransferId} />}
      {tab === 'payments' && <StudentPaymentsTab studentId={studentId} highlightPaymentId={highlightPaymentId} />}
      {tab === 'documents' && <StudentDocumentsTab studentId={studentId} />}
      {tab === 'final-checklist' && (
        <Box sx={{ mt: 2 }}>
          <FinalDocumentChecklistPanel studentId={studentId} />
        </Box>
      )}
      {tab === 'timeline' && <StudentTimelineTab studentId={studentId} />}
    </Box>
  );
};
